import { observable, action } from "mobx";
import isUndefined from "lodash/isUndefined";
import remove from "lodash/remove";
import queryString from "query-string";
import history from "util/history";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";
import SearchFilterModel from "./filters/SearchFilterModel";
import TypeaheadFilterModel from "./filters/TypeaheadFilterModel";
import SelectFilterModel from "./filters/SelectFilterModel";
import CheckboxFilterModel from "./filters/CheckboxFilterModel";
import forEach from "lodash/forEach";
import mapKeys from "lodash/mapKeys";
import filter from "lodash/filter";
import toLower from "lodash/toLower";
import includes from "lodash/includes";
import TableRowModel from "./TableRowModel";
import LoadingStates from "./LoadingStates";

class BaseTableModel {
    @observable itemsPerPage;
    @observable currentPage;
    @observable filters = observable.map({});
    @observable sorts = observable([]);

    constructor(props) {
        this.itemsPerPage = !isUndefined(props.itemsPerPage) ? props.itemsPerPage : 10;
        this.currentPage = !isUndefined(props.currentPage) ? props.currentPage : 0;
        this.definition = props.definition;
        this.dispatcher = null;
        this._instantiateFilterModels();
    }

    bindDispatcher(dispatcher) {
        this.dispatcher = dispatcher;
    }

    get isLoaded() {
        return true;
    }

    onMount(dispatcher) {
        this.bindDispatcher(dispatcher);

        if (this.loadState === LoadingStates.UI_NOT_READY) {
            this.loadState = LoadingStates.UI_READY;
        } else if (this.loadState === LoadingStates.DATA_REFRESH_NEEDED) {
            this.loadState = LoadingStates.UI_READY;
            this.refreshTable();
        }
    }

    @action
    changePage(newPage) {
        this.currentPage = newPage;
    }

    @action
    updateItemsPerPage(newPageSize) {
        this.itemsPerPage = newPageSize;
        this.currentPage = 0;
    }

    @action
    updateFilter(filterKey, newValue) {
        const filterModel = this.filters.get(filterKey);
        filterModel.updateValue(newValue);
        if (!filterModel.isNoOp) {
            // since we are looking at a new set of data, we want to reset to the first page
            this.currentPage = 0;
        }

        if (this.enableHistory && window && window.location) {
            let queryStringParams = queryString.parse(window.location.search);
            if (queryStringParams[filterKey] !== newValue) {
                queryStringParams[filterKey] = newValue;
                const query = isEmpty(queryStringParams) ? "" : "?" + queryString.stringify(queryStringParams);
                const newUrl = window.location.origin + window.location.pathname + query;
                history.pushState({}, "", newUrl);
            }
        }
    }

    @action
    updateSort(columnName) {
        let sortDescending = false;
        const oldSortConfig = remove(this.sorts, sortDefinition => sortDefinition.iteratee === columnName)[0];
        if (!isUndefined(oldSortConfig)) {
            sortDescending = !oldSortConfig.descending;
        }
        forEach(this.sorts, sort => { sort.primary = false; });
        this.sorts.push({ iteratee: columnName, descending: sortDescending, primary: true });
    }

    @action
    _instantiateFilterModels() {
        const filterModels = map(
            // TODO add support for other kinds of filters
            filter(this.filterOptions, filterDefinition => includes([
                "search",
                "select",
                "hidden",
                "checkbox",
                "typeahead"
            ], filterDefinition.type)),
            filterDefinition => {
                if (filterDefinition.type === "search") {
                    return new SearchFilterModel(filterDefinition);
                } else if (filterDefinition.type === "typeahead") {
                    return new TypeaheadFilterModel(filterDefinition);
                } else if (filterDefinition.type === "select" || filterDefinition.type === "hidden") {
                    return new SelectFilterModel(filterDefinition);
                } else if (filterDefinition.type === "checkbox") {
                    return new CheckboxFilterModel(filterDefinition);
                }
                // unsupported filter types should be filtered out above, should never get here.
                return null;
            }
        );

        forEach(filterModels, model => {
            this.filters.set(model.key, model);
        });
    }

    @action
    parseUrlSearchQuery(urlSearchQuery) {
        const queryStringParams = mapKeys(queryString.parse(urlSearchQuery), (value, key) => toLower(key));
        // filters is an observable map, use the builtin foreach
        this.filters.forEach((f, key) => {
            const filterValue = queryStringParams[toLower(key)] || f.defaultValue;
            f.updateValue(filterValue);
        });
        this.refreshTable();
    }

    @action
    refreshTable() {
        // noop - used by DynamicTableModel, kept here for consistency
    }

    get searchFilter() {
        return this.filters.get("search");
    }

    get columnOptions() {
        return this.definition.columnOptions;
    }

    get rowOptions() {
        return this.definition.rowOptions;
    }

    get filterOptions() {
        return this.definition.filterOptions;
    }

    get emptyTableFormatter() {
        return this.definition.emptyTableFormatter;
    }

    get enableHistory() {
        return this.definition.enableHistory || false;
    }

    createRowModel(data) {
        return new TableRowModel(data);
    }
}

export default BaseTableModel;
